/**
 * Cave Light Trigger Behavior
 * 
 * Creates an overhead spotlight with shadows that follows the player.
 * Used to light the player's path in dark areas.
 * 
 * MVP Features:
 * - Creates spotlight on player enter
 * - Spotlight follows player with smooth interpolation
 * - Shadows enabled for atmosphere
 * - Another trigger can destroy the light (find by entity discovery)
 * - Debug visualization showing light direction
 * - Fairy particle system hovering around the spotlight
 * 
 * ARCHITECTURE: Uses UnifiedLightManager's fixed pool to avoid shader recompilation.
 * All lights are acquired from the pool, not created directly.
 */

import { Vector3, Color3, Color4, ParticleSystem, DynamicTexture, AbstractMesh } from '@babylonjs/core';
import { EntityType } from '../../types/EntityTypes';
import type { TriggerBehavior } from '../../types/TriggerTypes';
import { logger } from '../../core/Logger';
import { UnifiedLightManager, type LightHandle } from '../../systems/UnifiedLightManager';

export const caveLightBehavior: TriggerBehavior = {
  id: 'trigger:cave_light',
  displayName: 'Cave Light',
  type: EntityType.TRIGGER_ZONE,
  category: 'trigger',
  tags: ['lighting', 'follow', 'shadows'],
  
  parameters: {
    lightColor: {
      type: 'color',
      default: [1.0, 0.95, 0.8],
      description: 'Light color (warm white default)'
    },
    lightIntensity: {
      type: 'number',
      default: 100.0,
      min: 10,
      max: 1000000.0,
      description: 'Light brightness (high values needed for PBR)'
    },
    heightOffset: {
      type: 'number',
      default: 3.0,
      min: 1.0,
      max: 20.0,
      description: 'Height above player'
    },
    followSpeed: {
      type: 'number',
      default: 3.0,
      min: 0.5,
      max: 15.0,
      description: 'How fast light follows player'
    },
    spotAngle: {
      type: 'number',
      default: 1.7,
      min: 0.3,
      max: 3.14,
      description: 'Spotlight cone angle (radians)'
    },
    lightRange: {
      type: 'number',
      default: 30.0,
      min: 5.0,
      max: 100.0,
      description: 'Maximum range of light illumination'
    },
    shadowEnabled: {
      type: 'boolean',
      default: true, // Shadows cast by characters/dynamic objects only (not level geometry)
      description: 'Enable shadow casting'
    },
    lightId: {
      type: 'string',
      default: 'cave_light',
      description: 'Unique ID for this light (used by destroy trigger)'
    },
    fairyParticles: {
      type: 'boolean',
      default: true,
      description: 'Enable fairy particle effect around the spotlight'
    },
    fairyColor: {
      type: 'color',
      default: [0.9, 1.0, 0.6],
      description: 'Fairy particle color (yellowish-green default)'
    },
    fairyCount: {
      type: 'number',
      default: 100,
      min: 5,
      max: 150,
      description: 'Number of fairy particles'
    },
    fillLightEnabled: {
      type: 'boolean',
      default: false,
      description: 'Enable fill point light in front of character'
    },
    fillLightIntensity: {
      type: 'number',
      default: 10.0,
      min: 10,
      max: 10000.0,
      description: 'Fill light brightness'
    },
    fillLightDistance: {
      type: 'number',
      default: 1.0,
      min: 0.5,
      max: 5.0,
      description: 'Distance in front of character for fill light'
    }
  },
  
  onEnter: (api) => {
    const { ctx, services } = api;
    const { params } = ctx;
    
    // Only react to player
    if (!ctx.entrant.isPlayer) return;
    
    // Check if light already exists (re-entry)
    if (ctx.self.metadata?.spotlightHandle) {
      logger.triggers.debug(`Cave light already exists for trigger ${ctx.self.id}`);
      return;
    }
    
    const player = services.entities.getPlayer();
    if (!player?.mesh) {
      logger.triggers.warn(`Cave light: No player mesh found`);
      return;
    }
    
    // Parse parameters with defaults from parameter definitions (single source of truth)
    const defs = caveLightBehavior.parameters!;
    const heightOffset = (params.heightOffset as number) ?? defs.heightOffset.default;
    const lightIntensity = (params.lightIntensity as number) ?? defs.lightIntensity.default;
    const followSpeed = (params.followSpeed as number) ?? defs.followSpeed.default;
    const spotAngle = (params.spotAngle as number) ?? defs.spotAngle.default;
    const lightRange = (params.lightRange as number) ?? defs.lightRange.default;
    const shadowEnabled = (params.shadowEnabled as boolean) ?? defs.shadowEnabled.default;
    const lightId = (params.lightId as string) ?? `cave_light_${ctx.self.id}`;
    const fairyParticles = (params.fairyParticles as boolean) ?? defs.fairyParticles.default;
    const fairyCount = (params.fairyCount as number) ?? defs.fairyCount.default;
    const fillLightEnabled = (params.fillLightEnabled as boolean) ?? defs.fillLightEnabled.default;
    const fillLightIntensity = (params.fillLightIntensity as number) ?? defs.fillLightIntensity.default;
    const fillLightDistance = (params.fillLightDistance as number) ?? defs.fillLightDistance.default;
    
    // Parse light color (could be array or object) - use parameter default as fallback
    const defaultLightColor = defs.lightColor.default as number[];
    const colorParam = params.lightColor ?? defaultLightColor;
    const lightColor = Array.isArray(colorParam) && colorParam.length >= 3
      ? new Color3(colorParam[0], colorParam[1], colorParam[2])
      : new Color3(defaultLightColor[0], defaultLightColor[1], defaultLightColor[2]);
    
    // Parse fairy color - use parameter default as fallback
    const defaultFairyColor = defs.fairyColor.default as number[];
    const fairyColorParam = params.fairyColor ?? defaultFairyColor;
    const fairyColor = Array.isArray(fairyColorParam) && fairyColorParam.length >= 3
      ? new Color4(fairyColorParam[0], fairyColorParam[1], fairyColorParam[2], 1.0)
      : new Color4(defaultFairyColor[0], defaultFairyColor[1], defaultFairyColor[2], 1.0);
    
    // Calculate initial position
    const playerPos = player.mesh.position;
    const lightPos = new Vector3(playerPos.x, playerPos.y + heightOffset, playerPos.z);
    const lightDir = new Vector3(0, -1, 0); // Point straight down
    
    // === Create Debug Arrow (pointing down to show light direction) ===
    // Using centralized DebugRenderer for consistent debug visuals
    // Offset arrow to the side so it doesn't block the spotlight cone
    const arrowOffset = new Vector3(1.0, 0, 0); // 1m to the right
    const debugArrow = services.debug.createArrow(lightPos.add(arrowOffset), lightDir, 1.5, lightColor);
    debugArrow.name = `${lightId}_debug_arrow`;
    
    // Make debug mesh not cast/receive shadows and mark for exclusion
    debugArrow.receiveShadows = false;
    if (debugArrow.metadata) {
      debugArrow.metadata.noShadows = true;
    } else {
      debugArrow.metadata = { noShadows: true };
    }
    
    // === Create Fairy Particle System ===
    let fairySystem: ParticleSystem | null = null;
    if (fairyParticles) {
      fairySystem = new ParticleSystem(`${lightId}_fairies`, fairyCount * 2, services.scene);
      
      // Emitter follows the light position
      fairySystem.emitter = lightPos.clone();
      
      // Particle lifetime - long lived for hovering effect
      fairySystem.minLifeTime = 1.0;
      fairySystem.maxLifeTime = 2.0;
      
      // Small glowing particles
      fairySystem.minSize = 0.05;
      fairySystem.maxSize = 0.15;
      
      // Slow random movement for hovering effect
      fairySystem.minEmitPower = 0.2;
      fairySystem.maxEmitPower = 0.8;
      
      // Directions - mostly horizontal with slight vertical
      fairySystem.direction1 = new Vector3(-1, -0.3, -1);
      fairySystem.direction2 = new Vector3(1, 0.5, 1);
      
      // No gravity - fairies float
      fairySystem.gravity = new Vector3(0, -1, 0);
      
      // Fairy colors - bright with glow fade
      fairySystem.color1 = fairyColor;
      fairySystem.color2 = new Color4(
        fairyColor.r * 0.8,
        fairyColor.g * 0.9,
        fairyColor.b,
        0.9
      );
      fairySystem.colorDead = new Color4(
        fairyColor.r * 0.5,
        fairyColor.g * 0.6,
        fairyColor.b * 0.3,
        0.0
      );
      
      // Additive blending for glow effect
      fairySystem.blendMode = ParticleSystem.BLENDMODE_ADD;
      
      // Sphere emitter around the light
      fairySystem.createSphereEmitter(0.1);
      
      // Slow emission rate for sparse fairy effect
      fairySystem.emitRate = fairyCount / 2;
      
      // Create soft glow texture
      const textureSize = 32;
      const glowTexture = new DynamicTexture(`${lightId}_fairy_tex`, textureSize, services.scene, false);
      const texCtx = glowTexture.getContext();
      
      const centerX = textureSize / 2;
      const centerY = textureSize / 2;
      const radius = textureSize / 2 - 1;
      
      const gradient = texCtx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
      gradient.addColorStop(0, 'rgba(255, 255, 255, 1.0)');
      gradient.addColorStop(0.3, 'rgba(255, 255, 200, 0.8)');
      gradient.addColorStop(1, 'rgba(255, 255, 150, 0.0)');
      
      texCtx.fillStyle = gradient;
      texCtx.fillRect(0, 0, textureSize, textureSize);
      
      glowTexture.update();
      glowTexture.hasAlpha = true;
      fairySystem.particleTexture = glowTexture;
      
      // Start the fairy system
      fairySystem.start();
      
      logger.triggers.debug(`✨ Fairy particles created for ${lightId}`);
    }
    
    // Create fill point light in front of character (using pool)
    let fillLightHandle: LightHandle | null = null;
    const fillLightId = `${lightId}_fill`;
    
    if (fillLightEnabled) {
      // Get player's facing direction (default to +Z if not available)
      const playerRotY = player.mesh.rotation?.y ?? 0;
      const forwardDir = new Vector3(Math.sin(playerRotY), 0, Math.cos(playerRotY));
      const fillLightPos = playerPos.add(forwardDir.scale(fillLightDistance)).add(new Vector3(0, 1.2, 0));
      
      // Track current position for smooth interpolation
      let currentFillPos = fillLightPos.clone();
      
      fillLightHandle = UnifiedLightManager.acquireCombatLight({
        position: fillLightPos,
        intensity: fillLightIntensity,
        range: 8,
        color: lightColor,
        
        onUpdate: (deltaTime, handle) => {
          const currentPlayer = services.entities.getPlayer();
          if (!currentPlayer?.mesh) return;
          
          // Get player's facing direction
          const rotY = currentPlayer.mesh.rotation?.y ?? 0;
          const forward = new Vector3(Math.sin(rotY), 0, Math.cos(rotY));
          const targetFillPos = currentPlayer.mesh.position
            .add(forward.scale(fillLightDistance))
            .add(new Vector3(0, 1.2, 0));
          
          // Smooth follow
          const t = Math.min(1, deltaTime * followSpeed);
          currentFillPos = Vector3.Lerp(currentFillPos, targetFillPos, t);
          handle.update({ position: currentFillPos });
        }
      });
      
      logger.triggers.debug(`💡 Fill light created: ${fillLightId} (pool slot: ${fillLightHandle.light?.name ?? 'NONE'})`);
    }
    
    // Create spotlight (using pool) with shadows on characters/dynamic objects only
    // Track current position for smooth interpolation
    let currentLightPos = lightPos.clone();
    let currentLightDir = lightDir.clone();
    
    const spotlightHandle = UnifiedLightManager.acquireSpotLight({
      position: lightPos,
      direction: lightDir,
      angle: spotAngle,
      exponent: 0, // Zero exponent = no falloff from center
      intensity: lightIntensity,
      range: lightRange,
      color: lightColor,
      shadowEnabled, // Enable shadows from characters/dynamic objects
      
      // Per-frame update to follow player
      onUpdate: (deltaTime, handle) => {
        const currentPlayer = services.entities.getPlayer();
        if (!currentPlayer?.mesh) return;
        
        // Calculate target position (above the player)
        const playerPos = currentPlayer.mesh.position;
        const targetPos = playerPos.add(new Vector3(0, heightOffset, 0));
        
        // Smooth follow
        const t = Math.min(1, deltaTime * followSpeed);
        currentLightPos = Vector3.Lerp(currentLightPos, targetPos, t);
        
        // Update light direction to point at character position (not straight down)
        // This creates a trailing spotlight effect when the light lags behind
        const directionToPlayer = playerPos.subtract(currentLightPos);
        if (directionToPlayer.length() > 0.01) {
          currentLightDir = directionToPlayer.normalize();
        }
        
        handle.update({ position: currentLightPos, direction: currentLightDir });
        
        // Update debug arrow position and direction to match light
        const arrow = services.scene.getMeshByName(`${lightId}_debug_arrow`) as AbstractMesh | null;
        if (arrow) {
          arrow.position = currentLightPos.add(new Vector3(1.0, 0, 0));
          // Rotate arrow to match light direction using lookAt
          const lookTarget = arrow.position.add(currentLightDir.scale(2));
          arrow.lookAt(lookTarget);
          // Arrow mesh points +Y by default, rotate to align with direction
          arrow.rotation.x += Math.PI / 2;
        }
        
        // Update fairy emitter position
        const fairies = services.scene.getParticleSystemByID(`${lightId}_fairies`);
        if (fairies) {
          fairies.emitter = currentLightPos.clone();
        }
      }
    });
    
    // Store handles and references in metadata for cleanup
    if (!ctx.self.metadata) {
      (ctx.self as { metadata: Record<string, unknown> }).metadata = {};
    }
    ctx.self.metadata!.spotlightHandle = spotlightHandle;
    ctx.self.metadata!.lightId = lightId;
    ctx.self.metadata!.debugArrow = debugArrow;
    ctx.self.metadata!.fairySystem = fairySystem;
    ctx.self.metadata!.fillLightHandle = fillLightHandle;
    ctx.self.metadata!.fillLightId = fillLightId;
    
    logger.triggers.log(`🔦 Cave light created: ${lightId} (pool slot: ${spotlightHandle.light?.name ?? 'NONE'}, fairies: ${fairyParticles}, fill: ${fillLightEnabled})`);
  },
  
  onExit: (api) => {
    const { ctx } = api;
    
    // Only react to player
    if (!ctx.exiter.isPlayer) return;
    
    // Don't destroy on exit - light persists until destroy trigger
    // This allows the light to stay on while player is in the cave
    logger.triggers.debug(`Player exited cave light trigger ${ctx.self.id} - light persists`);
  }
};

/**
 * Destroy Light Trigger Behavior
 * 
 * Destroys a light created by cave_light behavior.
 * Use the lightId parameter to specify which light to destroy.
 */
export const destroyLightBehavior: TriggerBehavior = {
  id: 'trigger:destroy_light',
  displayName: 'Destroy Light',
  type: EntityType.TRIGGER_ZONE,
  category: 'trigger',
  tags: ['lighting', 'destroy'],
  
  parameters: {
    lightId: {
      type: 'string',
      default: 'cave_light',
      description: 'ID of the light to destroy (matches cave_light lightId)'
    }
  },
  
  onEnter: (api) => {
    const { ctx, services } = api;
    const { params } = ctx;
    
    // Only react to player
    if (!ctx.entrant.isPlayer) return;
    
    const targetLightId = (params.lightId as string) ?? 'cave_light';
    
    // Helper function to clean up debug mesh and particles
    const cleanupExtras = (lightId: string) => {
      // Clean up debug arrow
      const debugArrow = services.scene.getMeshByName(`${lightId}_debug_arrow`);
      if (debugArrow) {
        services.debug.remove(debugArrow);
        logger.triggers.debug(`🔷 Debug arrow disposed for ${lightId}`);
      }
      
      // Clean up fairy particle system
      const fairySystem = services.scene.getParticleSystemByID(`${lightId}_fairies`);
      if (fairySystem) {
        fairySystem.dispose();
        logger.triggers.debug(`✨ Fairy particles disposed for ${lightId}`);
      }
    };
    
    // Find all triggers that might have created this light
    const allEntities = services.entities.getAll();
    for (const entity of allEntities) {
      if (entity.metadata?.lightId === targetLightId) {
        // Clean up debug arrow from metadata if stored
        const debugArrow = entity.metadata.debugArrow as AbstractMesh | undefined;
        if (debugArrow && !debugArrow.isDisposed()) {
          services.debug.remove(debugArrow);
          delete entity.metadata.debugArrow;
        }
        
        // Clean up fairy system from metadata if stored
        const fairySystem = entity.metadata.fairySystem as ParticleSystem | undefined;
        if (fairySystem) {
          fairySystem.dispose();
          delete entity.metadata.fairySystem;
        }
        
        // Release fill light handle (returns to pool)
        const fillLightHandle = entity.metadata.fillLightHandle as LightHandle | undefined;
        if (fillLightHandle) {
          fillLightHandle.release();
          delete entity.metadata.fillLightHandle;
          delete entity.metadata.fillLightId;
          logger.triggers.debug(`💡 Fill light released for ${targetLightId}`);
        }
        
        // Release spotlight handle (returns to pool)
        const spotlightHandle = entity.metadata.spotlightHandle as LightHandle | undefined;
        if (spotlightHandle) {
          spotlightHandle.release();
          delete entity.metadata.spotlightHandle;
          delete entity.metadata.lightId;
          logger.triggers.log(`🔦 Light '${targetLightId}' released (with debug mesh, fairies, and fill light)`);
          return;
        }
      }
    }
    
    // Fallback: just clean up extras (light is pooled, nothing to dispose directly)
    cleanupExtras(targetLightId);
    logger.triggers.log(`🔦 Light '${targetLightId}' cleanup (extras only - light was pooled)`);
  }
};
